import React, { useEffect, useRef, useState } from 'react';
import Header from '../../components/Header';
import { useParams } from 'react-router-dom';
import { useNavigate, Navigate } from 'react-router-dom';
// Bubble CSS kept for the `.ql-editor` styles used by the direct HTML render.
import 'react-quill-new/dist/quill.bubble.css';
// Override Quill 2.x margin:0 → khôi phục khoảng cách đoạn (import SAU bubble.css).
import './post-content.css';
import postApi from '../../api/postApi';
import { normalizeForView } from '../../utils/normalizePostContent';
import neutralizeInlineColors from '../../utils/neutralize-inline-colors';
import { useThemeMode } from '../../theme/theme-provider';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import OpenInAppButton from '../../components/OpenInAppButton';
import AnswerQuestion from '../../components/Q&A/Q&A';

function PostPage() {
    const { postid } = useParams();
    const [post, setPost] = useState();
    const { userInfo } = useSelector((state) => state.auth);
    const navigate = useNavigate();

    const deltePost = async () => {
        if (!postid) return;
        try {
            await postApi.deletePost(postid);
            navigate('/post');
        } catch (error) {
            console.log(error);
        }
    };

    // Bài viết cũ có màu chữ đen viết cứng trong HTML — ở chế độ tối thành chữ
    // đen trên nền đen. Khử màu trung tính sau khi nội dung đã vào DOM.
    const contentRef = useRef(null);
    const { isDark } = useThemeMode();

    useEffect(() => {
        neutralizeInlineColors(contentRef.current, isDark);
    }, [isDark, post]);

    useEffect(() => {
        const fetchPost = async () => {
            try {
                // Không filter theo category='guide' nữa: bài kênh Cộng Đồng có
                // category khác (hashtag danh mục) — cần lấy được post để đọc
                // post.channel rồi redirect đúng trang chi tiết bên dưới.
                const { data } = await postApi.getGuidePost(`postId=${postid}`);
                setPost(data.post[0]);
            } catch (error) {
                console.log(error);
            }
        };
        fetchPost();
    }, [postid]);

    // Share link cũ /post/:id vẫn hoạt động cho bài Cộng Đồng — redirect sang
    // trang chi tiết đúng kênh (defensive: chỉ redirect khi field channel có mặt).
    if (post?.channel === 'community') {
        return <Navigate to={`/cong-dong/${postid}`} replace />;
    }

    return (
        <div>
            <Header />
            {post && (
                <div className="flex flex-col justify-between h-full md:w-[768px] lg:w-[1024px] md:mx-auto pt-16 gap-2">
                    {userInfo?.role === 'admin' && (
                        <div className="p-2 flex gap-2 text-xl justify-end">
                            <FontAwesomeIcon
                                className="p-2 text-content-3 hover:text-content cursor-pointer"
                                icon={faEdit}
                                onClick={() =>
                                    navigate('/post/post_editor/' + postid)
                                }
                            />
                            <FontAwesomeIcon
                                className="p-2 text-content-3 hover:text-content cursor-pointer"
                                icon={faTrash}
                                onClick={deltePost}
                            />
                        </div>
                    )}
                    {/* Webview Zalo/FB không tự mở app — nút bắn scheme longhousee://post/:id */}
                    <div className="px-4 pt-3">
                        <OpenInAppButton path={`/post/${postid}`} autoAttempt />
                    </div>
                    <h3 className="p-4 font-bold">{post?.title}</h3>
                    <div className="w-full p-4">
                        {/* Blurred copy fills the 16:9 frame; real image shown
                            uncropped (object-contain) on top — no crop, no gaps. */}
                        <div className="relative w-full aspect-video overflow-hidden rounded-md bg-surface-muted">
                            <img
                                aria-hidden="true"
                                src={`${process.env.REACT_APP_API_PUBLIC_URL}/img/post-thumbs/${post.imagePath}`}
                                className="absolute inset-0 w-full h-full object-cover blur-xl scale-110"
                                alt=""
                            />
                            <img
                                src={`${process.env.REACT_APP_API_PUBLIC_URL}/img/post-thumbs/${post.imagePath}`}
                                className="relative w-full h-full object-contain"
                                alt="banner"
                            />
                        </div>
                    </div>
                    {/*
                      Render the stored HTML DIRECTLY (not through ReactQuill).
                      ReactQuill re-parses HTML -> Delta -> HTML, which mangled
                      the content (split links, re-added <p> tags) so the page
                      never matched what's in the DB. Direct render shows exactly
                      what was saved. We keep the `.ql-editor` class + bubble CSS
                      so fonts/sizes/lists still style correctly.
                    */}
                    <div className="p-0 overflow-auto">
                        <div
                            ref={contentRef}
                            className="ql-editor break-words [&_a]:break-all [&_a]:text-blue-600 [&_a]:underline"
                            dangerouslySetInnerHTML={{
                                __html: normalizeForView(post.content),
                            }}
                        />
                    </div>
                    <div className="flex gap-1 justify-end text-sm italic text-content p-4">
                        <span>Ngày tạo:</span>
                        <span>
                            {format(post.createTime, 'dd/MM/yyy HH:mm')}
                        </span>
                    </div>
                </div>
            )}
            <AnswerQuestion postId={postid} />
        </div>
    );
}

export default PostPage;
